import React, { ChangeEvent, useEffect, useRef } from 'react';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSearchContext } from '../../context/SearchProvider';
import AutocompleteMenuItem from '../AutocompleteMenuItem';
import useDebounce from '../../hooks/useDebounce';
import Autocomplete from '../Autocomplete';
import AutocompleteRow from './AutocompleteRow';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { track } from '../../lib/util/tracking';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import {
  bg0,
  bgBlue,
  bgPrimaryText,
  text0,
  text3,
  text4,
} from '@osmosis/tailwind-ui/styles';
import { useEmbedContext } from '@osmosis/nextjs-components/context/EmbedProvider';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  getSearchContainerClasses,
  searchPlaceholderMessage,
} from './searchStyles';

type Props = {
  placeholder?: string;
  containerClasses?: string;
  dataTestId?: string;
  focusOnMount?: boolean;
};

const SearchInputActions = ({ onSearch }: { onSearch: () => void }) => {
  const { search, clearSearch } = useSearchContext();
  const intl = useIntl();

  if (!search?.length) return null;

  const clearLabel = intl.formatMessage({
    id: 'searchResults.clearButton',
    defaultMessage: 'Clear search',
  });

  const searchLabel = intl.formatMessage({
    id: 'dashboard.welcomeHeader.searchBtn',
    defaultMessage: 'Search',
  });

  return (
    <Box data-testid="search-text-component" className="min-w-auto ">
      <Flex className="items-center">
        <Button
          data-testid="search-clear-input"
          tabIndex={0}
          lang={intl.locale}
          aria-label={clearLabel}
          className={`${text3} p-0.5 mr-2 text-xl hover:${text4} rounded-md bg-transparent hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
          onClick={clearSearch}
          variant="transparent"
          onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              clearSearch();
            }
          }}
        >
          <Icon icon={faTimes} aria-hidden="true" />
        </Button>
        <Box className="mx-1" data-testid="search-btn">
          <Button
            lang={intl.locale}
            aria-labelledby="search-btn-label"
            aria-label={searchLabel}
            data-testid="search-btn-button"
            className={`h-[35px] rounded-md ${bgBlue} ${text0} px-4 hover:${bgPrimaryText}`}
            onClick={onSearch}
          >
            <span id="search-btn-label" className="not-sr-only">
              <FormattedMessage
                id="dashboard.welcomeHeader.searchBtn"
                defaultMessage="Search"
              />
            </span>
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};
/**
 * Search.
 */
const Search = (props: Props): JSX.Element => {
  const {
    placeholder: placeholderProp,
    containerClasses = getSearchContainerClasses(),
    dataTestId,
    focusOnMount,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();

  const { getPageLocation } = useEmbedContext();
  const { pathname } = getPageLocation();

  const placeholder =
    placeholderProp || intl.formatMessage(searchPlaceholderMessage);

  const {
    fetchAutocomplete,
    autocompleteResults,
    search,
    setSearchValue,
    setSearchQuery,
    openSearchResults,
    isSearchFieldFocused,
    handleSearchFieldFocus,
  } = useSearchContext();

  const debouncedValue = useDebounce(search, 500);

  const handleChange = (e: ChangeEvent<Element>): void => {
    const event = e as ChangeEvent<HTMLInputElement>;
    const { value } = event.currentTarget;
    setSearchValue(value);
  };

  const trackCta = () => {
    track(
      'view_cta',
      {
        page_path: pathname,
        type: 'purchase',
        placement: 'search-results-top',
        label: 'Join millions of students who learn by Osmosis!',
      },
      ['keen'],
    );
  };

  const handleSearch = (value?: string) => {
    openSearchResults();
    const stringValue = typeof value === 'string' ? value : void 0;
    if (stringValue?.length) {
      setSearchValue(stringValue);
    }
    setSearchQuery(stringValue || search);
    trackCta();
    handleSearchFieldFocus(false);
  };

  const handleOnFocus = () => handleSearchFieldFocus(true);
  const handleBlur = () => {
    setTimeout(() => {
      handleSearchFieldFocus(false);
    }, 200);
  };

  useEffect(() => {
    fetchAutocomplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  useEffect(() => {
    if (focusOnMount) inputRef.current?.focus();
  }, [focusOnMount]);

  const searchLabel = intl.formatMessage({
    id: 'header.search.aria_label.dashboard_search_input',
    defaultMessage: 'Search for a topic',
  });

  const focusStyles = isSearchFieldFocused
    ? 'shadow-[0_3px_5px_-1px_rgba(0,0,0,.2),0_5px_8px_0_rgba(0,0,0,.14),0_1px_14px_0_rgba(0,0,0,.12)]'
    : '';

  const searchClasses = cn(
    'w-full max-w-[740px] items-center justify-center',
    focusStyles,
    containerClasses,
  );

  return (
    <Flex
      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
      ref={containerRef}
      data-testid="search-wrapper"
      className={searchClasses}
    >
      <Flex className="relative w-full p-1">
        <Box
          className={`absolute left-4 top-1/2 z-10 -translate-y-1/2 transform`}
        >
          <Icon icon={faMagnifyingGlass} className={text3} />
        </Box>

        <Autocomplete
          onSelect={handleSearch}
          onChange={handleChange}
          items={autocompleteResults}
          keyExtractor={(item) => item}
          value={search}
          setValue={setSearchValue}
          placeholder={placeholder}
          dataTestid={dataTestId || 'search'}
          inputProps={{
            'aria-label': searchLabel,
            className: `pl-9 h-9 w-full ${bg0} rounded-md focus:outline-none`,
            onFocusInput: handleOnFocus,
            onBlur: handleBlur,
            ref: inputRef,
          }}
          menuProps={{
            style: {
              position: 'absolute',
              width: containerRef.current?.offsetWidth || '100%',
              overflow: 'auto',
              top: '100%',
              left: '-4px',
              padding: '10px',
              maxHeight: 'calc(100vh - 50px)',
              zIndex: ZIndex.HEADER,
              marginTop: 1,
              borderRadius: '0 0 5px 5px',
              // border: `'1px solid ${colors.gray30}`,
              boxShadow:
                '0 5px 5px -1px rgb(0 0 0 / 20%), 0 8px 8px 0 rgb(0 0 0 / 14%), 0 14px 14px 0 rgb(0 0 0 / 12%)',
            },
          }}
          renderItem={(item, hasFocus) => (
            <AutocompleteMenuItem
              text={
                <Flex className="items-center">
                  <Box className="mr-2">
                    <Icon icon={faSearch} className={`${text3} text-xs`} />
                  </Box>
                  <AutocompleteRow text={item} searchText={search} />
                </Flex>
              }
              focused={hasFocus}
            />
          )}
        />
      </Flex>
      <SearchInputActions onSearch={handleSearch} />
    </Flex>
  );
};

export default Search;
